import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, latestUpdates } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const comic = useMemo(() => comics.find((c) => c.slug === slug), [slug]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[70vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 mb-4">该漫画不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 6);
  const comicChapters = latestUpdates
    .filter((u) => u.comicSlug === comic.slug)
    .sort((a, b) => b.chapterId - a.chapterId);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li><Link to="/category/all" className="hover:text-primary-600 transition-colors">分类浏览</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li><span className="text-foreground-800 font-medium">{comic.title}</span></li>
            </ol>
          </div>
        </nav>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[280px] shrink-0">
                <div className="rounded-lg overflow-hidden bg-background-100 aspect-[3/4] w-full md:w-[280px]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 无删减韩国漫画完整版`}
                    title={`${comic.title} - 汗汗漫画网下拉式免费阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                    comic.status === '已完结'
                      ? 'bg-accent-100 text-accent-900'
                      : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="font-semibold">{comic.rating}</span>
                    <span className="text-foreground-400">分</span>
                  </span>
                  <span className="text-sm text-foreground-500">共{comic.chapters}话</span>
                  <span className="text-sm text-foreground-500">{comic.views.toLocaleString()} 次阅读</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {['完整版', '无删减', '下拉观看', '免费阅读', '韩国漫画', '高清画质'].map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>
                <p className="text-sm text-foreground-500 mb-6">{comic.author}</p>

                <Link
                  to={`/comic/${comic.slug}/chapter/1`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white text-base font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  <span>开始阅读</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-6">
              章节列表
            </h2>

            {comicChapters.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {comicChapters.map((ch) => (
                  <Link
                    key={ch.chapterId}
                    to={`/comic/${comic.slug}/chapter/${ch.chapterId}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/40 transition-all cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800 font-medium truncate">{ch.chapterTitle}</span>
                    <span className="text-xs text-foreground-400 ml-2 whitespace-nowrap">{ch.updateTime.split(' ')[0]}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-foreground-500 text-center py-10">暂无章节</p>
            )}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}